import { ModelBadge } from "@/components/ModelBadge";
import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Waveform, ArrowLeft, MusicNotes, Clock, Play, Pause, DownloadSimple, Lock, X, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";
import { registerAudioPlay, clearAudioRegistration } from "@/lib/audioManager";

import { API } from "@/lib/apiBase";

const formatTime = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const SongPlayer = ({ song, onPreviewBlocked }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(song.duration || 0);
  const [blocked, setBlocked] = useState(false);
  const isPreviewLimited = !!(song.is_trial && !song.is_unlocked);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => {
      setTime(a.currentTime);
      if (isPreviewLimited && a.currentTime >= 45) {
        a.pause();
        a.currentTime = 30;
        setPlaying(false);
        setBlocked(true);
        clearAudioRegistration(a);
        if (onPreviewBlocked) onPreviewBlocked();
      }
    };
    const onLoad = () => setDur(a.duration || song.duration || 0);
    const onEnd = () => { setPlaying(false); clearAudioRegistration(a); };
    const onPause = () => setPlaying(false);
    const onError = (e) => { console.error("Audio error:", e); };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onLoad);
    a.addEventListener("ended", onEnd);
    a.addEventListener("pause", onPause);
    a.addEventListener("error", onError);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onLoad);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("error", onError);
      clearAudioRegistration(a);
    };
  }, [song, isPreviewLimited, onPreviewBlocked]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    // Hard-cap: once past 45s preview, block resume and fire the modal (Bug #1)
    if (isPreviewLimited && (a.currentTime >= 45 || blocked)) {
      setBlocked(true);
      if (onPreviewBlocked) onPreviewBlocked();
      return;
    }
    if (playing) {
      a.pause();
      clearAudioRegistration(a);
    } else {
      registerAudioPlay(a);
      a.play().catch(() => toast.error("Unable to play. Try downloading instead."));
    }
    setPlaying(!playing);
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a) return;
    const maxT = isPreviewLimited ? 45 : dur;
    const v = Math.min(parseFloat(e.target.value), maxT);
    a.currentTime = v;
    setTime(v);
    if (isPreviewLimited && v >= 45) {
      a.pause();
      setPlaying(false);
      setBlocked(true);
      if (onPreviewBlocked) onPreviewBlocked();
    }
  };

  return (
    <div>
      <audio
        ref={audioRef}
        src={song.id ? `${API}/songs/stream/${song.id}` : (song.song_url || song.stream_url)}
        preload="metadata"
        onError={() => toast.error("This song's audio source is temporarily unavailable. Please contact support — we'll send it manually.")}
      />
      <div className="flex items-center gap-3">
        <button onClick={toggle} className="w-10 h-10 rounded-full bg-[#F59E0B] flex items-center justify-center text-black hover:bg-[#D97706] transition-all shrink-0" data-testid={`play-${song.id}`}>
          {playing ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}
        </button>
        <div className="flex-1 min-w-0">
          <input type="range" min="0" max={isPreviewLimited ? 45 : (dur || 100)} step="0.1" value={time} onChange={seek} className="w-full h-1.5 rounded-full appearance-none cursor-pointer" style={{ background: `linear-gradient(to right, #F59E0B ${(time / (isPreviewLimited ? 45 : (dur || 100))) * 100}%, rgba(255,255,255,0.1) 0%)` }} />
          <div className="flex justify-between text-xs mt-0.5" style={{ color: "#A1A1AA" }}>
            <span>{formatTime(time)}</span>
            <span>{formatTime(isPreviewLimited ? 45 : dur)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const LyricsModal = ({ song, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.75)" }} onClick={onClose} data-testid="lyrics-modal">
    <div className="bg-[#141417] border border-[#F59E0B]/20 rounded-2xl max-w-lg w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between p-5 border-b border-white/5">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold mb-1" style={{ color: "#F59E0B" }}>Lyrics</p>
          <h3 className="text-white font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>{song.song_name || "Untitled Song"}</h3>
        </div>
        <button onClick={onClose} className="text-white/40 hover:text-white transition-colors" data-testid="close-lyrics-modal"><X size={22} /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-5">
        <pre className="whitespace-pre-wrap text-white/90 text-base leading-loose font-normal" style={{ fontFamily: "'Manrope', sans-serif" }}>{song.lyrics || "No lyrics saved for this song."}</pre>
      </div>
      <div className="p-4 border-t border-white/5 flex justify-end">
        <button onClick={onClose} className="bg-[#F59E0B] text-black font-semibold rounded-full px-5 py-2 text-sm hover:bg-[#D97706] transition-all" data-testid="close-lyrics-btn">Close</button>
      </div>
    </div>
  </div>
);

const SongHistoryPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [lyricsModalSong, setLyricsModalSong] = useState(null);
  const [previewBlocked, setPreviewBlocked] = useState(false);

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    fetchSongs();
  }, [user]);

  const fetchSongs = async (q = "") => {
    try {
      const res = await axios.get(`${API}/songs/history${q ? `?search=${encodeURIComponent(q)}` : ""}`, { withCredentials: true });
      setSongs(res.data.songs || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setLoading(true);
    fetchSongs(search);
  };

  const formatDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <div className="min-h-screen" style={{ background: "#0A0A0C" }}>
      {lyricsModalSong && <LyricsModal song={lyricsModalSong} onClose={() => setLyricsModalSong(null)} />}
      <header className="relative z-10 backdrop-blur-xl bg-[#0A0A0C]/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-5">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="text-white/60 hover:text-white transition-colors" data-testid="history-back-btn"><ArrowLeft size={24} /></button>
            <div className="flex items-center gap-3">
              <Waveform size={28} weight="bold" style={{ color: "#F59E0B" }} />
              <span className="text-xl font-light tracking-tight text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Song History</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-8 lg:px-12 py-12">
        {/* Search + Filter */}
        <form onSubmit={handleSearch} className="mb-3">
          <div className="bg-[#141417] border border-white/5 rounded-xl focus-within:border-[#F59E0B] transition-colors flex items-center px-4">
            <MusicNotes size={18} style={{ color: "#A1A1AA" }} />
            <input data-testid="song-search" type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by song name or lyrics..." className="w-full bg-transparent px-3 py-3 text-white placeholder:text-gray-500 outline-none text-sm" style={{ fontFamily: "'Manrope', sans-serif" }} />
            <button type="submit" className="text-[#F59E0B] text-sm font-medium shrink-0">Search</button>
          </div>
        </form>
        <div className="mb-6" />
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-[#F59E0B] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : songs.length === 0 ? (
          <div className="bg-[#141417] border border-white/5 rounded-2xl p-12 text-center">
            <MusicNotes size={48} weight="thin" style={{ color: "#A1A1AA" }} className="mx-auto mb-4" />
            <h3 className="text-xl text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>No songs yet</h3>
            <p className="mb-6" style={{ color: "#A1A1AA", fontFamily: "'Manrope', sans-serif" }}>Generate your first song from the home page</p>
            <button onClick={() => navigate("/")} className="bg-[#F59E0B] text-black font-semibold rounded-full px-8 py-3 hover:bg-[#D97706] transition-all" data-testid="go-home-btn">Create a Song</button>
          </div>
        ) : (
          <div className="space-y-4">
            {songs.map((song) => (
              <div key={song.id} className="bg-[#141417] border border-white/5 hover:border-white/15 transition-all rounded-2xl p-5" data-testid={`history-song-${song.id}`}>
                <div className="flex items-start gap-4 mb-4">
                  {song.image_url ? (
                    <img src={song.image_url} alt={`Cover art for Telugu AI song ${song.song_name || 'Untitled'}`} className="w-14 h-14 rounded-xl object-cover shrink-0" loading="lazy" />
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#D97706] flex items-center justify-center shrink-0">
                      <MusicNotes size={24} weight="fill" className="text-black" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{song.song_name || "Untitled Song"}</p>
                    {song.lyrics && <p className="text-white/60 text-xs line-clamp-1 mb-1" style={{ fontFamily: "'Manrope', sans-serif" }}>{song.lyrics.substring(0, 80)}...</p>}
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-400">{song.status}</span>
                      <ModelBadge song={song} />
                      {song.is_trial && !song.is_unlocked && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[#F59E0B]/10 text-[#F59E0B]"><Lock size={10} className="inline mr-1" />45s Preview</span>
                      )}
                      {song.duration > 0 && <span className="text-white/30 text-xs">{formatTime(song.duration)}</span>}
                      <span className="flex items-center gap-1 text-white/30 text-xs"><Clock size={12} />{formatDate(song.created_at)}</span>
                    </div>
                  </div>
                </div>

                {/* Player */}
                {(song.song_url || song.stream_url) && <SongPlayer song={song} onPreviewBlocked={() => setPreviewBlocked(true)} />}

                {/* Actions */}
                <div className="flex items-center gap-3 mt-4 flex-wrap">
                  {song.is_trial && !song.is_unlocked ? (
                    <Link to="/pricing" className="bg-[#F59E0B] text-black font-semibold rounded-full px-5 py-2 text-xs hover:bg-[#D97706] transition-all inline-flex items-center gap-1.5" data-testid={`unlock-${song.id}`}>
                      <Lock size={12} weight="bold" /> Subscribe to Unlock
                    </Link>
                  ) : song.song_url ? (
                    <a href={`${API}/songs/download/${song.id}`} className="bg-[#F59E0B] text-black font-semibold rounded-full px-5 py-2 text-xs hover:bg-[#D97706] transition-all inline-flex items-center gap-1.5" data-testid={`download-${song.id}`}>
                      <DownloadSimple size={12} weight="bold" /> Download
                    </a>
                  ) : null}
                  {song.lyrics && (
                    <button onClick={() => setLyricsModalSong(song)} className="bg-transparent text-white border border-white/20 font-medium rounded-full px-5 py-2 text-xs hover:bg-white/5 transition-all inline-flex items-center gap-1.5" data-testid={`view-lyrics-${song.id}`}>
                      <FileText size={12} weight="bold" /> View Lyrics
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Preview Blocked Modal (Bug #1 fix) */}
      {previewBlocked && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.8)" }} data-testid="preview-blocked-modal">
          <div className="bg-[#141417] border border-[#F59E0B]/40 rounded-2xl max-w-md w-full p-8 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#F59E0B]/10 flex items-center justify-center mb-4">
              <Lock size={28} weight="bold" style={{ color: "#F59E0B" }} />
            </div>
            <h3 className="text-xl text-white font-medium mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Subscription Required</h3>
            <p className="text-sm mb-6" style={{ color: "#A1A1AA", fontFamily: "'Manrope', sans-serif" }}>
              Your free 45-second preview has ended. Subscribe to unlock this song plus unlimited Telugu AI songs.
            </p>
            <div className="flex flex-col gap-2">
              <button onClick={() => { setPreviewBlocked(false); navigate("/pricing"); }} className="bg-[#F59E0B] text-black font-semibold rounded-full px-6 py-3 hover:bg-[#D97706] transition-all" data-testid="preview-subscribe-btn">View Plans</button>
              <button onClick={() => setPreviewBlocked(false)} className="bg-transparent text-white/60 rounded-full px-6 py-2 text-sm hover:text-white transition-colors" data-testid="preview-close-btn">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SongHistoryPage;
